<script setup lang="ts">
import {
  FontAwesomeIcon,
  type FontAwesomeIconProps,
} from "@fortawesome/vue-fontawesome";
import { icons, type IconName } from "@/plugins/fontawesome";
import { computed, useAttrs } from "vue";

type IconProps = {
  name: IconName;
  rounded?: boolean;
  size?: FontAwesomeIconProps["size"];
  color?: string;
};

const sizeMap: Record<NonNullable<IconProps["size"]>, string> = {
  "2xs": "1rem",
  xs: "1.25rem",
  sm: "1.5rem",
  lg: "2rem",
  xl: "2.5rem",
  "2xl": "3rem",
  "1x": "2rem",
  "2x": "3rem",
  "3x": "4rem",
  "4x": "5rem",
  "5x": "6rem",
  "6x": "7rem",
  "7x": "8.5rem",
  "8x": "9.5rem",
  "9x": "11rem",
  "10x": "12rem",
};

const {
  name,
  rounded,
  size = "lg",
  color = "var(--clr-n8)",
} = defineProps<IconProps>();
const attrs = useAttrs();

const wrapperStyle = computed(() => {
  const dim = sizeMap[size] || "2rem";
  return {
    width: dim,
    height: dim,
    backgroundColor: color,
  };
});
</script>

<template>
  <div class="rounded" :style="wrapperStyle" v-if="rounded">
    <font-awesome-icon
      :icon="icons[name]"
      color="white"
      :size="size"
      :v-bind="attrs"
    />
  </div>
  <font-awesome-icon
    v-else
    :icon="icons[name]"
    :color="color"
    :size="size"
    v-bind="$attrs"
  />
</template>

<style scoped lang="scss">
.rounded {
  background-color: v-bind(color);
  border-radius: 9999px;
  padding: 5px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
